import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { validate } from 'uuid'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

import {
  selectWebAppStateCompanyUserId,
  selectWebAppStateMySelf,
  selectWebAppStateMySelfHasAnalyticsAccess,
} from '../../store/getters/web-app.getters'
import { setShowSnackBar } from '../../store/reducers/web-app.reducer'
import { buildRelativePath, PathConfig } from '../../core/config/PathConfig'
import { analyticsDetailV2StateGetReportByIdAction } from '../../store/actions/analytics-details/analytic-details-v2.actions'
import AnalyticDetailsLayout from '../../components/analytics-details-v2/AnalyticDetailsLayout'
import {
  selectAnalyticDetailsStateAnalyticDetails,
  selectAnalyticDetailsStateIsLoadingGetAnalyticDetails,
} from '../../store/getters/analytics-details/analytic-details-v2.getters'
import { reinitializeAnalyticDetailsV2State } from '../../store/reducers/analytics-details/analytic-details-v2.reducer'
import { AnalyticsTypeEnum } from '../../core/enums/analytics-type.enum'
import GlDialog from '../../components/share/dialog/GlDialog'
import AnalyticsDashboardShareDialog from '../../components/analytics-dashboard/more-actions/share-analytics/AnalyticsDashboardShareDialog'
import { patchReportService } from '../../core/api/services/bookmetrie/reports.service'
import { AnalyticsStatusEnum } from '../../core/enums/analytics-status.enum'
import { AnalyticsPermissionEnum } from '../../core/enums/analytics-permission.enum'
import AnalyticDetailsReadershipProfileDetailsView from '../../components/analytics-details-v2/view/readership-profile/details/AnalyticDetailsReadershipProfileDetailsView'

const PrivateReadershipProfileView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { reportId: urlReportId } = useParams<{ reportId: string }>()

  const [shareDialog, setShareDialog] = useState<boolean>(false)

  const [reportId, setReportId] = useState<string>(urlReportId as string)

  const dispatch = useAppDispatch()

  const isLoadingGetAnalyticDetails = useAppSelector(
    selectAnalyticDetailsStateIsLoadingGetAnalyticDetails,
  )
  const companyUserId = useAppSelector(selectWebAppStateCompanyUserId)
  const analyticDetails = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetails,
  )
  const mySelf = useAppSelector(selectWebAppStateMySelf)
  const hasAnalyticsAccess = useAppSelector(
    selectWebAppStateMySelfHasAnalyticsAccess,
  )

  useEffect(() => {
    if (mySelf && !hasAnalyticsAccess) {
      dispatch(setShowSnackBar(t('webApp.notAccessToFeature')))
      navigate(buildRelativePath([PathConfig.BOOKMETRIE]))
    }
  }, [mySelf, hasAnalyticsAccess])

  useEffect(() => {
    setReportId(urlReportId as string)
  }, [urlReportId])

  // Au chargement de la page
  useEffect(() => {
    if (hasAnalyticsAccess) {
      if (validate(reportId)) {
        void dispatch(analyticsDetailV2StateGetReportByIdAction({ reportId }))
      }
    }

    return () => {
      dispatch(reinitializeAnalyticDetailsV2State())
    }
  }, [reportId])

  const handleRenameAnalytic = (newName: string) => {
    if (newName === analyticDetails.name) return
    // TODO : cet endpoint a pas l'air de fonctionner
    patchReportService(analyticDetails.id, { name: newName })
      .then(() => {
        dispatch(
          setShowSnackBar(
            t('snackbarMessage.renameReportConfirm', {
              reportName: newName,
            }),
          ),
        )
      })
      .catch(() => {
        dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
      })
  }

  return (
    <>
      <AnalyticDetailsLayout
        handleRenameAnalytic={handleRenameAnalytic}
        analyticName={analyticDetails?.name || ''}
        sharedBy={[
          analyticDetails?.shared_by?.first_name,
          analyticDetails?.shared_by?.last_name,
        ]
          .join(' ')
          .trim()}
        disableEdit={
          analyticDetails?.status !== AnalyticsStatusEnum.GENERATED ||
          analyticDetails.permission === AnalyticsPermissionEnum.READ
        }
        hasButtonPrevious={false}
        isLoading={isLoadingGetAnalyticDetails}
        handleExitAnalyticDetails={() => {
          return navigate(
            buildRelativePath([
              PathConfig.BOOKMETRIE,
              PathConfig.PRIVATE_REPORTS,
            ]),
          )
        }}
      >
        {(analyticDetails?.id && validate(reportId) && (
          <AnalyticDetailsReadershipProfileDetailsView
            handleShareAnalytics={() => setShareDialog(true)}
            isPrivate
          />
        )) ||
          null}
      </AnalyticDetailsLayout>
      <GlDialog open={shareDialog} onClose={() => setShareDialog(false)}>
        {analyticDetails && (
          <AnalyticsDashboardShareDialog
            analyticsName={analyticDetails?.name}
            analyticsId={analyticDetails?.id}
            owner={analyticDetails?.owner}
            companyUserId={companyUserId}
            slug={analyticDetails?.slug}
            analyticsType={AnalyticsTypeEnum.READERSHIP_PROFILE}
          />
        )}
      </GlDialog>
    </>
  )
}
export default PrivateReadershipProfileView
